import {
  Compressed,
  compress,
  decompress,
  trimUndefinedRecursively,
} from 'compress-json'
import cloneDeep from 'lodash/cloneDeep'

const DEBUG = false

export type OpsBase = { op: string; args: JSONValue }[]

export type StackItem<Ops extends OpsBase> = {
  redo: Ops
  undo: Ops
}

export const isStackItem = <Ops extends OpsBase>(
  item: unknown
): item is StackItem<Ops> => {
  return (
    typeof item === 'object' &&
    item !== null &&
    'redo' in item &&
    'undo' in item &&
    Array.isArray(item.redo) &&
    Array.isArray(item.undo)
  )
}

export type UndoManagerParams<Ops extends OpsBase> = {
  processStackItem: (
    intent: 'undo' | 'redo',
    item: StackItem<Ops>
  ) => Promise<'should-replay' | 'ok' | void>
  maxSize?: number
  persist?: {
    version: number
    storage: 'localStorage' | 'sessionStorage'
    key: string
  }
}

export function createUndoManager<Ops extends OpsBase>(
  params: UndoManagerParams<Ops>
) {
  let stack: Array<StackItem<Ops>> = []
  let activeIndex: number = 0
  const can = {
    get undo() {
      return stack[activeIndex - 1] !== undefined
    },
    get redo() {
      return stack[activeIndex] !== undefined
    },
  }

  type Event = 'undo' | 'redo'
  const listeners = new Map<Event, Set<() => void>>()

  function emit(event: Event) {
    listeners.get(event)?.forEach((listener) => listener())
  }

  function on(event: Event, listener: () => void) {
    if (!listeners.has(event)) {
      listeners.set(event, new Set())
    }
    listeners.get(event)!.add(listener)
    return () => {
      listeners.get(event)?.delete(listener)
    }
  }

  type Stored = {
    version: number
    stack: Array<StackItem<Ops>>
    activeIndex: number
    compressed?: boolean
  }

  const storage = {
    hydrate: () => {
      if (!params.persist) return
      const localStack = window[params.persist.storage].getItem(
        params.persist.key
      )
      if (!localStack) return
      let parsed: Stored | undefined
      try {
        parsed = JSON.parse(localStack) as Stored
        if (parsed.compressed) {
          parsed.stack = decompress(
            parsed.stack as unknown as Compressed
          ) as Array<StackItem<Ops>>
        }
      } catch (e) {
        console.error(
          `error recovering stack from ${params.persist.storage}: ${e}`
        )
        return
      }
      if (!parsed || parsed.version !== params.persist.version) return
      stack = parsed.stack
      activeIndex = parsed.activeIndex
      if (DEBUG) {
        console.log(
          `Hydrated undo stack: ${stack.length} items, active index: ${activeIndex}`
        )
      }
    },
    save: () => {
      if (!params.persist) return
      window[params.persist.storage].setItem(
        params.persist.key,
        JSON.stringify({
          compressed: true,
          version: params.persist.version,
          activeIndex,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          stack: compress(stack) as any,
        } satisfies Stored)
      )
      if (DEBUG) {
        console.log(
          `Saved undo stack: ${stack.length} items, active index: ${activeIndex}`
        )
      }
    },
  }

  function push(_item: StackItem<Ops>) {
    const item = cloneDeep(_item)
    trimUndefinedRecursively(item)
    stack = stack.slice(0, activeIndex)
    stack.push(item)
    if (stack.length > (params.maxSize ?? 500)) {
      stack.shift()
    }
    activeIndex = stack.length
    storage.save()
    if (DEBUG) {
      console.log(`Pushed to undo stack`, { item, stack })
    }
  }

  async function undo() {
    const stackItem = stack[activeIndex - 1]
    if (!stackItem) {
      if (DEBUG) {
        console.log(
          'Undo attempted but no item available at index',
          activeIndex - 1
        )
      }
      return
    }
    if (DEBUG) {
      console.log(
        `Undoing ${JSON.stringify(stackItem, null, 2)} at index ${
          activeIndex - 1
        }`
      )
    }
    const result = await params.processStackItem('undo', stackItem)
    if (result === 'should-replay') {
      // as if never happened
      return
    }
    activeIndex--
    storage.save()
    emit('undo')
  }

  async function redo() {
    const stackItem = stack[activeIndex]
    if (!stackItem) {
      if (DEBUG) {
        console.log(
          'Redo attempted but no item available at index',
          activeIndex
        )
      }
      return
    }
    if (DEBUG) {
      console.log(
        `Redoing ${JSON.stringify(stackItem, null, 2)} at index ${activeIndex}`
      )
    }
    const result = await params.processStackItem('redo', stackItem)
    if (result === 'should-replay') {
      // as if never happened
      return
    }
    activeIndex++
    storage.save()
    emit('redo')
  }

  storage.hydrate()

  return { stack, push, undo, redo, can, storage, on }
}

export type UndoManager<Ops extends OpsBase> = ReturnType<
  typeof createUndoManager<Ops>
>

// helpers

export type JSONValue =
  | null
  | string
  | boolean
  | number
  | Array<JSONValue>
  | ReadonlyArray<JSONValue>
  | JSONObject

type JSONObject = { [key: string]: JSONValue | undefined }
